import { cn } from "@/lib/utils";
import type { BrandSpinnerProps } from "@/types/ui/brand-spinner";

/**
 * BrandSpinner — the lightweight, neutral Help Genie loading state.
 *
 * The Help Genie logo sits inside a spinning brand-gradient arc. Deliberately
 * NOT the summoned-mascot GenieLoader: this is for first-impression / pre-content
 * moments (the app-boot auth check, entering a live agent) where the big mascot
 * "summon" flashing before the real Genie reads as a redundant double-Genie.
 * Reach for GenieLoader inside the signed-in app; reach for this on the way in.
 */
const BrandSpinner = ({ label, className }: BrandSpinnerProps) => (
  <div className={cn("min-h-dvh flex flex-col items-center justify-center bg-[#F9FAFC]", className)}>
    <div className="relative flex items-center justify-center w-[120px] h-[120px]">
      {/* Brand-gradient arc spinning around the logo's circumference. */}
      <svg
        className="absolute inset-0 w-full h-full animate-spin"
        viewBox="0 0 100 100"
        style={{ animationDuration: "1.4s" }}
        aria-hidden
      >
        <defs>
          <linearGradient id="brand-spinner-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4E9CFF" />
            <stop offset="100%" stopColor="#FF4DB8" />
          </linearGradient>
        </defs>
        <circle
          cx="50"
          cy="50"
          r="46"
          fill="none"
          stroke="url(#brand-spinner-grad)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="72 218"
        />
      </svg>
      <img
        src="/helpgenie_logo.png"
        alt=""
        className="w-[72px] h-[72px] object-contain relative z-10 select-none pointer-events-none"
        draggable={false}
      />
    </div>
    {label && <p className="mt-5 font-sans text-sm font-medium tracking-wide text-genie-muted">{label}</p>}
  </div>
);

export default BrandSpinner;
